import { type App } from 'vue';

export interface RouterParams {
  path?: string;
  query?: Record<string, string>;
  params?: Record<string, string>;
}

interface Route {
  query: URLSearchParams | [];
  params: Record<string, string>;
  path: string;
  url: string;
}

export type Router = {
  linkActiveClass: string;
  scrollBehavior: () => { y: number };
  push: (params: string | RouterParams, dontReload?: boolean) => void;
  open: (params: string | RouterParams) => void;
  go: (steps: number) => void;
  route: Route;
  assembleUrl: (params: string | RouterParams) => string;
  removeQueryParams: () => void;
};

export const router: Router = {
  linkActiveClass: 'is-active',
  scrollBehavior: () => ({ y: 0 }),
  push(params, dontReload = false) {
    if (!import.meta.env.SSR) {
      if (!dontReload) {
        window.location.href = this.assembleUrl(params);
      } else {
        window.history.pushState({ pageTitle: document.title }, '', this.assembleUrl(params));
      }
    }
  },
  open(params) {
    if (!import.meta.env.SSR) {
      const url = window.location.origin + this.assembleUrl(params);
      window.open(url, '_blank');
    }
  },
  go(steps) {
    if (!import.meta.env.SSR) {
      window.history.go(steps);
    }
  },
  route: {
    query: !import.meta.env.SSR ? new URLSearchParams(window.location.search) : new URLSearchParams(),
    params: {} as Record<string, string>,
    path: '',
    url: '',
  },
  assembleUrl(params) {
    let url = '';
    if (typeof params === 'string') {
      url = params;
    } else if (typeof params === 'object') {
      if (params.path) {
        url = params.path;
      }
      if (params.query) {
        for (const fieldName in params.query) {
          if (Object.prototype.hasOwnProperty.call(params.query, fieldName)) {
            url += (!url.match(/\?/) ? '?' : '&') + fieldName + '=' + encodeURIComponent(params.query[fieldName]);
          }
        }
      }
      if (params.params) {
        for (const fieldName in params.params) {
          url = url.replace(':' + fieldName, encodeURIComponent(params.params[fieldName]));
        }
      }
    }
    return url;
  },
  removeQueryParams() {
    if (!import.meta.env.SSR) {
      const newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  },
};

interface PageContext {
  routeParams: Record<string, string>;
  urlParsed: { search: URLSearchParams | [] };
  urlPathname: string;
  urlOriginal: string;
}

declare module '@vue/runtime-core' {
  interface App {
    router: Router;
    $router: Router;
    $route: Route;
  }
}

export const RouterPlugin = {
  install: (app: App, pageContext: PageContext) => {
    router.route.params = pageContext.routeParams;
    router.route.query = pageContext.urlParsed.search;
    router.route.path = pageContext.urlPathname;
    router.route.url = pageContext.urlOriginal;

    app.router = router;
    app.$router = router;
    app.$route = router.route;
    app.config.globalProperties.$route = router.route;
    app.config.globalProperties.$router = router;
  },
};
